import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditNoteIcon from '@mui/icons-material/EditNote'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Navigate, useNavigate } from 'react-router-dom'

import { CHARACTERS_ROOT_TAG_ID } from '../../../shared/tags/canonicalCharacterTags'
import type { TagDefinition } from '../../../shared/tags/tagRegistry'
import { collectDescendantIds, getTagDisplayPath } from '../../../shared/tags/tagRegistry'
import CharacterNoteDialog from '../components/invitations/CharacterNoteDialog'
import InvitationManager from '../components/invitations/InvitationManager'
import { InvitationShareDialog } from '../components/invitations/InvitationShareDialog'
import OpenRegistrationCard from '../components/invitations/OpenRegistrationCard'
import { useAuth } from '../contexts/AuthContext'
import { InvitationListItem, UserDirectoryItem, apiService } from '../services/api'

const cellSx = {
  py: 1.5,
  px: 2,
  fontSize: '0.84rem',
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-subtle)',
}

const headerCellSx = {
  ...cellSx,
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  py: 1,
  userSelect: 'none' as const,
}

const statusStyles: Record<string, { label: string; dotColor: string }> = {
  pending: { label: 'Pending', dotColor: '#f59e0b' },
  claimed: { label: 'Claimed', dotColor: '#22c55e' },
  expired: { label: 'Expired', dotColor: '#94a3b8' },
}

/** True when no tag lists `tagId` as a parent — i.e. a leaf in the tag graph. */
function isLeafTag(tags: Record<string, TagDefinition>, tagId: string): boolean {
  for (const def of Object.values(tags)) {
    if ((def.parentIds || []).includes(tagId)) {
      return false
    }
  }
  return true
}

function formatUserLabel(u: UserDirectoryItem): string {
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
  return name || u.email
}

function pickPrimaryCharacterInvitation(
  invites: InvitationListItem[],
  linkedUser: UserDirectoryItem | undefined,
  invById: Map<string, InvitationListItem>,
): InvitationListItem | undefined {
  if (invites.length === 0) {
    return undefined
  }
  if (linkedUser?.invitedVia?.id) {
    const via = invById.get(linkedUser.invitedVia.id)
    if (via && invites.some((i) => i.id === via.id)) {
      return via
    }
  }
  const pending = invites
    .filter((i) => i.status === 'pending')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  if (pending.length > 0) {
    return pending[0]
  }
  return [...invites].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
}

type UnifiedRow =
  | {
      kind: 'character_leaf'
      key: string
      tagId: string
      path: string
      name: string
      user?: UserDirectoryItem
      invites: InvitationListItem[]
      primaryInv?: InvitationListItem
    }
  | { kind: 'account'; key: string; user: UserDirectoryItem }
  | { kind: 'account_orphan_character'; key: string; user: UserDirectoryItem; pathLabel: string }
  | { kind: 'pending_regular'; key: string; inv: InvitationListItem }
  | { kind: 'character_orphan'; key: string; inv: InvitationListItem; pathLabel: string }

/** Rows where an invitation exists (sent) sort above empty character slots / accounts with no invite. */
function rowHasInvitationSent(row: UnifiedRow): boolean {
  switch (row.kind) {
    case 'character_leaf':
      return row.invites.length > 0
    case 'account':
    case 'account_orphan_character':
      return row.user.invitedVia != null
    case 'pending_regular':
    case 'character_orphan':
      return true
    default:
      return false
  }
}

function sortRowsInvitationFirst(rows: UnifiedRow[]): UnifiedRow[] {
  return [...rows].sort((a, b) => {
    const ha = rowHasInvitationSent(a)
    const hb = rowHasInvitationSent(b)
    if (ha !== hb) {
      return ha ? -1 : 1
    }
    return 0
  })
}

const mobileCardSx = {
  py: 1.25,
  px: 1.25,
  borderRadius: 2,
  border: '1px solid var(--border-subtle)',
  bgcolor: 'var(--bg-surface)',
}

const mobileSep = (
  <Box component="span" sx={{ color: 'var(--text-tertiary)', mx: 0.5 }} aria-hidden>
    ·
  </Box>
)

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function StatusDotLabel({ statusKey }: { statusKey: string }) {
  const st = statusStyles[statusKey] || statusStyles.pending
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }} />
      {st.label}
    </Box>
  )
}

/** Muted styling for invite recipient names (distinct from tag paths and primary labels). */
const inviteRecipientNameSx = { color: 'var(--text-secondary)', fontWeight: 400 } as const

function QrShareInviteButton({ onClick, iconSize }: { onClick: () => void; iconSize: number }) {
  return (
    <Tooltip title="Open link and QR code">
      <IconButton
        size="small"
        aria-label="Open link and QR code"
        onClick={onClick}
        sx={{ color: 'var(--icon-default)' }}
      >
        <QrCode2Icon sx={{ fontSize: iconSize }} />
      </IconButton>
    </Tooltip>
  )
}

function DeleteUserButton({
  onClick,
  iconSize,
  disabled,
  disabledReason,
}: {
  onClick: () => void
  iconSize: number
  disabled?: boolean
  disabledReason?: string
}) {
  const tooltip = disabled ? disabledReason || 'Cannot delete' : 'Delete user account'
  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size="small"
          aria-label="Delete user account"
          onClick={onClick}
          disabled={disabled}
          sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
        >
          <PersonRemoveIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const PeoplePage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [bookIdForInvites, setBookIdForInvites] = useState<string | null>(null)
  const [noteTarget, setNoteTarget] = useState<{ tagId: string; name: string } | null>(null)
  const [tags, setTags] = useState<Record<string, TagDefinition>>({})
  const [users, setUsers] = useState<UserDirectoryItem[]>([])
  const [invitations, setInvitations] = useState<InvitationListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [tagInviteRequest, setTagInviteRequest] = useState<{ nonce: number; tagId: string; name: string } | null>(null)
  const [shareInvite, setShareInvite] = useState<InvitationListItem | null>(null)
  const [inviteDeleteConfirmId, setInviteDeleteConfirmId] = useState<string | null>(null)
  const [inviteDeleteSubmitting, setInviteDeleteSubmitting] = useState(false)
  const [userDeleteConfirmId, setUserDeleteConfirmId] = useState<string | null>(null)
  const [userDeleteSubmitting, setUserDeleteSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const bookRes = await apiService.getTheLastManifesto()
      const bookId = bookRes.success && bookRes.data?._id ? bookRes.data._id : null
      setBookIdForInvites(bookId)

      const [registryRes, usersRes, invRes] = await Promise.all([
        bookId ? apiService.getTagRegistry(bookId) : Promise.resolve({ success: false as const }),
        apiService.listUsers(),
        apiService.listInvitations(),
      ])

      if (registryRes.success) {
        setTags(registryRes.data.tags)
      }
      if (usersRes.success) {
        setUsers(usersRes.data)
      }
      if (invRes.success) {
        setInvitations(invRes.data)
      }
    } catch {
      toast.error('Failed to load people data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!user || (user.role !== 'author' && user.role !== 'admin')) {
      return
    }
    void loadData()
  }, [authLoading, user, loadData])

  const userByCharacterTagId = useMemo(() => {
    const m = new Map<string, UserDirectoryItem>()
    for (const u of users) {
      if (u.characterTagId) {
        m.set(u.characterTagId, u)
      }
    }
    return m
  }, [users])

  const invById = useMemo(() => new Map(invitations.map((i) => [i.id, i])), [invitations])

  const charInvitesByTag = useMemo(() => {
    const m = new Map<string, InvitationListItem[]>()
    for (const inv of invitations) {
      if (inv.type === 'character' && inv.characterTagId) {
        const arr = m.get(inv.characterTagId) || []
        arr.push(inv)
        m.set(inv.characterTagId, arr)
      }
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    }
    return m
  }, [invitations])

  const unifiedRows = useMemo((): UnifiedRow[] => {
    const accounts: UnifiedRow[] = users
      .filter((u) => !u.characterTagId)
      .sort((a, b) => a.email.localeCompare(b.email))
      .map((u) => ({ kind: 'account' as const, key: `acct-${u.id}`, user: u }))

    const pendingRegular: UnifiedRow[] = invitations
      .filter((inv) => inv.type === 'regular' && inv.status === 'pending')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .map((inv) => ({ kind: 'pending_regular' as const, key: `reg-${inv.id}`, inv }))

    const hasCharRoot = !!tags[CHARACTERS_ROOT_TAG_ID]
    const descendantIds = hasCharRoot ? collectDescendantIds(tags, CHARACTERS_ROOT_TAG_ID) : new Set<string>()
    const leafIds = Array.from(descendantIds).filter((id) => id !== CHARACTERS_ROOT_TAG_ID && isLeafTag(tags, id))
    const leafSet = new Set(leafIds)

    const accountOrphanCharacter: UnifiedRow[] = users
      .filter((u) => u.characterTagId && !leafSet.has(u.characterTagId))
      .sort((a, b) => a.email.localeCompare(b.email))
      .map((u) => {
        const tid = u.characterTagId!
        const pathLabel = tags[tid] ? getTagDisplayPath(tags, tid) : tid
        return { kind: 'account_orphan_character' as const, key: `acct-orph-${u.id}`, user: u, pathLabel }
      })

    if (!hasCharRoot) {
      return sortRowsInvitationFirst([...accounts, ...accountOrphanCharacter, ...pendingRegular])
    }

    const leafRows: UnifiedRow[] = leafIds
      .map((tagId) => {
        const path = getTagDisplayPath(tags, tagId)
        const name = tags[tagId]?.name || tagId
        const linked = userByCharacterTagId.get(tagId)
        const invites = charInvitesByTag.get(tagId) || []
        const primaryInv = pickPrimaryCharacterInvitation(invites, linked, invById)
        return {
          kind: 'character_leaf' as const,
          key: `leaf-${tagId}`,
          tagId,
          path,
          name,
          user: linked,
          invites,
          primaryInv,
        }
      })
      .sort((a, b) => a.path.localeCompare(b.path, 'he'))

    const orphanCharacter: UnifiedRow[] = invitations
      .filter((inv) => inv.type === 'character' && inv.characterTagId && !leafSet.has(inv.characterTagId))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .map((inv) => {
        const tid = inv.characterTagId!
        const pathLabel = tags[tid] ? getTagDisplayPath(tags, tid) : tid
        return { kind: 'character_orphan' as const, key: `orph-${inv.id}`, inv, pathLabel }
      })

    return sortRowsInvitationFirst([
      ...leafRows,
      ...accounts,
      ...accountOrphanCharacter,
      ...pendingRegular,
      ...orphanCharacter,
    ])
  }, [tags, users, invitations, userByCharacterTagId, charInvitesByTag, invById])

  const handleInviteForTag = (tagId: string, name: string) => {
    setTagInviteRequest({ nonce: Date.now(), tagId, name })
  }

  const handleTagInviteHandled = () => {
    setTagInviteRequest(null)
  }

  const refreshInvitations = useCallback(async () => {
    const invRes = await apiService.listInvitations()
    if (invRes.success) {
      setInvitations(invRes.data)
    }
  }, [])

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/invite/${token}`
    void navigator.clipboard.writeText(url)
    toast.success('Link copied')
  }

  const handleDeleteInvitation = async (id: string): Promise<boolean> => {
    try {
      await apiService.deleteInvitation(id)
      setShareInvite((prev) => (prev?.id === id ? null : prev))
      await refreshInvitations()
      toast.success('Invitation deleted')
      return true
    } catch {
      toast.error('Failed to delete invitation')
      return false
    }
  }

  const invitePendingDelete = inviteDeleteConfirmId ? invById.get(inviteDeleteConfirmId) : undefined

  const handleConfirmDeleteInvitation = async () => {
    if (!inviteDeleteConfirmId) {
      return
    }
    setInviteDeleteSubmitting(true)
    try {
      const ok = await handleDeleteInvitation(inviteDeleteConfirmId)
      if (ok) {
        setInviteDeleteConfirmId(null)
      }
    } finally {
      setInviteDeleteSubmitting(false)
    }
  }

  const userPendingDelete = userDeleteConfirmId ? users.find((u) => u.id === userDeleteConfirmId) : undefined

  const handleConfirmDeleteUser = async () => {
    if (!userDeleteConfirmId) {
      return
    }
    setUserDeleteSubmitting(true)
    try {
      await apiService.deleteUser(userDeleteConfirmId)
      toast.success('User deleted')
      setUserDeleteConfirmId(null)
      await loadData()
    } catch (err) {
      const message =
        (err as { message?: string; response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Failed to delete user'
      toast.error(message)
    } finally {
      setUserDeleteSubmitting(false)
    }
  }

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size={28} sx={{ color: 'var(--text-tertiary)' }} />
      </Box>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.role !== 'author' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={28} sx={{ color: 'var(--text-tertiary)' }} />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2, md: 4 }, pb: { xs: 6, md: 8 } }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: { xs: 'center', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
            People
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.8rem', md: '0.88rem' },
              color: 'var(--text-tertiary)',
              mt: 0.5,
              maxWidth: 720,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Rows with at least one invitation sent appear first; within each group, order is leaf characters, accounts
            without a character, accounts with a stale character tag, pending regular invites, then non-leaf character
            invites.
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, ml: 'auto' }}>
          <InvitationManager
            embedded
            hideInvitationList
            invitationsFromParent={invitations}
            bookId={bookIdForInvites}
            tagInviteRequest={tagInviteRequest}
            onTagInviteRequestHandled={handleTagInviteHandled}
            onInvitationsChanged={refreshInvitations}
          />
        </Box>
      </Box>

      <OpenRegistrationCard />

      <Box
        sx={{
          border: '1px solid var(--border-subtle)',
          borderRadius: 2,
          bgcolor: 'var(--bg-surface)',
          overflow: isMobile ? 'visible' : 'auto',
        }}
      >
        {unifiedRows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
            <Typography sx={{ fontSize: '0.9rem', color: 'var(--text-tertiary)' }}>
              Nothing to show yet — add leaf tags under דמויות or create invitations.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Stack spacing={1} sx={{ p: { xs: 1, sm: 1.5 } }}>
            {unifiedRows.map((row) => {
              if (row.kind === 'character_leaf') {
                const extraInvites = row.primaryInv
                  ? row.invites.filter((i) => i.id !== row.primaryInv!.id).length
                  : row.invites.length
                return (
                  <Box key={row.key} sx={mobileCardSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        rowGap: 0.5,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        sx={{
                          flex: '1 1 120px',
                          minWidth: 0,
                          fontSize: '0.9rem',
                          lineHeight: 1.35,
                          textAlign: 'left',
                        }}
                        dir="auto"
                      >
                        <Box component="span" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {row.path}
                        </Box>
                        {row.primaryInv && (
                          <Box component="span" sx={inviteRecipientNameSx}>
                            {' · '}
                            {row.primaryInv.recipientName}
                            {extraInvites > 0 ? ` (+${extraInvites})` : ''}
                          </Box>
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignItems: 'center',
                          flexShrink: 0,
                          ml: 'auto',
                        }}
                      >
                        {row.invites.length === 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            onClick={() => handleInviteForTag(row.tagId, row.name)}
                          >
                            Invite
                          </Button>
                        )}
                        {bookIdForInvites && (
                          <>
                            <Tooltip title="Write about this character">
                              <IconButton
                                size="small"
                                aria-label="Write about this character"
                                onClick={() => setNoteTarget({ tagId: row.tagId, name: row.name })}
                                sx={{ color: 'var(--text-tertiary)', '&:hover': { color: 'var(--icon-hover)' } }}
                              >
                                <EditNoteIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open this character's page">
                              <IconButton
                                size="small"
                                aria-label="Open this character's page"
                                onClick={() => navigate(`/character/${bookIdForInvites}/${row.tagId}`)}
                                sx={{ color: 'var(--text-tertiary)', '&:hover': { color: 'var(--icon-hover)' } }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {row.primaryInv && (
                          <QrShareInviteButton onClick={() => setShareInvite(row.primaryInv!)} iconSize={18} />
                        )}
                        {row.user && (
                          <DeleteUserButton
                            onClick={() => setUserDeleteConfirmId(row.user!.id)}
                            iconSize={18}
                            disabled={row.user.id === user.id}
                            disabledReason="You cannot delete your own account"
                          />
                        )}
                      </Box>
                    </Box>
                    {row.user ? (
                      <Typography
                        sx={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', mb: 0.75, lineHeight: 1.4 }}
                        dir="auto"
                      >
                        {row.user.email}
                        {mobileSep}
                        {formatUserLabel(row.user)}
                        {mobileSep}
                        {row.user.role}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', mb: 0.75 }}>
                        No account linked
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}
                      dir="auto"
                      component="div"
                    >
                      {row.primaryInv ? (
                        <>
                          {row.primaryInv.type === 'character' ? 'Character' : 'Regular'}
                          {mobileSep}
                          <StatusDotLabel statusKey={row.primaryInv.status} />
                          {mobileSep}
                          {formatShortDate(row.primaryInv.createdAt)}
                        </>
                      ) : (
                        <Box component="span" sx={{ color: 'var(--text-tertiary)' }}>
                          No invitation
                        </Box>
                      )}
                    </Typography>
                  </Box>
                )
              }
              if (row.kind === 'account') {
                const u = row.user
                const via = u.invitedVia?.id ? invById.get(u.invitedVia.id) : undefined
                return (
                  <Box key={row.key} sx={mobileCardSx}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1 }}>
                      {via && (
                        <>
                          <Tooltip title="Copy invite link">
                            <IconButton
                              size="small"
                              onClick={() => copyLink(via.token)}
                              sx={{ color: 'var(--icon-default)' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete invitation">
                            <IconButton
                              size="small"
                              onClick={() => setInviteDeleteConfirmId(via.id)}
                              sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <DeleteUserButton
                        onClick={() => setUserDeleteConfirmId(u.id)}
                        iconSize={18}
                        disabled={u.id === user.id}
                        disabledReason="You cannot delete your own account"
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', mb: 0.5 }}>
                      No character
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        color: 'var(--text-secondary)',
                        mb: via ? 0.75 : 0,
                        lineHeight: 1.4,
                      }}
                      dir="auto"
                    >
                      {u.email}
                      {mobileSep}
                      {formatUserLabel(u)}
                      {mobileSep}
                      {u.role}
                    </Typography>
                    {via && (
                      <Typography
                        sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}
                        dir="auto"
                        component="div"
                      >
                        <Box component="span" sx={{ color: 'var(--text-tertiary)' }}>
                          Invite{' '}
                        </Box>
                        <Box component="span" sx={inviteRecipientNameSx}>
                          {via.recipientName}
                        </Box>
                        {mobileSep}
                        {via.type === 'character' ? 'Character' : 'Regular'}
                        {mobileSep}
                        <StatusDotLabel statusKey={via.status} />
                        {mobileSep}
                        {formatShortDate(via.createdAt)}
                      </Typography>
                    )}
                  </Box>
                )
              }
              if (row.kind === 'pending_regular') {
                const inv = row.inv
                return (
                  <Box key={row.key} sx={mobileCardSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        rowGap: 0.5,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        sx={{
                          flex: '1 1 120px',
                          minWidth: 0,
                          fontSize: '0.9rem',
                          lineHeight: 1.35,
                          textAlign: 'left',
                          ...inviteRecipientNameSx,
                        }}
                      >
                        {inv.recipientName}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignItems: 'center',
                          flexShrink: 0,
                          ml: 'auto',
                        }}
                      >
                        <QrShareInviteButton onClick={() => setShareInvite(inv)} iconSize={18} />
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', mb: 0.75 }}>
                      Regular · pending
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem' }} component="div">
                      <StatusDotLabel statusKey={inv.status} />
                      {mobileSep}
                      {formatShortDate(inv.createdAt)}
                    </Typography>
                  </Box>
                )
              }
              if (row.kind === 'account_orphan_character') {
                const u = row.user
                const via = u.invitedVia?.id ? invById.get(u.invitedVia.id) : undefined
                return (
                  <Box key={row.key} sx={mobileCardSx}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1 }}>
                      {via && (
                        <>
                          <Tooltip title="Copy invite link">
                            <IconButton
                              size="small"
                              onClick={() => copyLink(via.token)}
                              sx={{ color: 'var(--icon-default)' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete invitation">
                            <IconButton
                              size="small"
                              onClick={() => setInviteDeleteConfirmId(via.id)}
                              sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <DeleteUserButton
                        onClick={() => setUserDeleteConfirmId(u.id)}
                        iconSize={18}
                        disabled={u.id === user.id}
                        disabledReason="You cannot delete your own account"
                      />
                    </Box>
                    <Typography
                      sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', mb: 0.25 }}
                      dir="auto"
                    >
                      {row.pathLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', mb: 0.75 }}>
                      stale character tag
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        color: 'var(--text-secondary)',
                        mb: via ? 0.75 : 0,
                        lineHeight: 1.4,
                      }}
                      dir="auto"
                    >
                      {u.email}
                      {mobileSep}
                      {formatUserLabel(u)}
                      {mobileSep}
                      {u.role}
                    </Typography>
                    {via && (
                      <Typography
                        sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}
                        dir="auto"
                        component="div"
                      >
                        <Box component="span" sx={{ color: 'var(--text-tertiary)' }}>
                          Invite{' '}
                        </Box>
                        <Box component="span" sx={inviteRecipientNameSx}>
                          {via.recipientName}
                        </Box>
                        {mobileSep}
                        {via.type === 'character' ? 'Character' : 'Regular'}
                        {mobileSep}
                        <StatusDotLabel statusKey={via.status} />
                        {mobileSep}
                        {formatShortDate(via.createdAt)}
                      </Typography>
                    )}
                  </Box>
                )
              }
              const inv = row.inv
              const claimantName = inv.claimedBy
                ? `${inv.claimedBy.firstName || ''} ${inv.claimedBy.lastName || ''}`.trim()
                : ''
              return (
                <Box key={row.key} sx={mobileCardSx}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      rowGap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        flex: '1 1 120px',
                        minWidth: 0,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: 1.35,
                        textAlign: 'left',
                      }}
                      dir="auto"
                    >
                      {row.pathLabel}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'center',
                        flexShrink: 0,
                        ml: 'auto',
                      }}
                    >
                      <QrShareInviteButton onClick={() => setShareInvite(inv)} iconSize={18} />
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', mb: 0.75 }}>
                    non-leaf character tag
                  </Typography>
                  {inv.claimedBy && (
                    <Typography sx={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', mb: 0.75 }} dir="auto">
                      {inv.claimedBy.email}
                      {claimantName ? (
                        <>
                          {mobileSep}
                          {claimantName}
                        </>
                      ) : null}
                    </Typography>
                  )}
                  <Typography
                    sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}
                    dir="auto"
                    component="div"
                  >
                    <Box component="span" sx={{ color: 'var(--text-tertiary)' }}>
                      Invite{' '}
                    </Box>
                    <Box component="span" sx={inviteRecipientNameSx}>
                      {inv.recipientName}
                    </Box>
                    {mobileSep}
                    Character
                    {mobileSep}
                    <StatusDotLabel statusKey={inv.status} />
                    {mobileSep}
                    {formatShortDate(inv.createdAt)}
                  </Typography>
                </Box>
              )
            })}
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', minWidth: 140, width: 160 }}>
                    Actions
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', minWidth: 160 }}>
                    Character
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', minWidth: 140 }}>
                    Email
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', minWidth: 120 }}>
                    Name
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', width: 72 }}>
                    Role
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', minWidth: 120 }}>
                    Invitation
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', width: 88 }}>
                    Inv. type
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', width: 96 }}>
                    Status
                  </Box>
                  <Box component="th" sx={{ ...headerCellSx, textAlign: 'left', width: 96 }}>
                    Date
                  </Box>
                </Box>
              </Box>
              <Box component="tbody">
                {unifiedRows.map((row) => {
                  if (row.kind === 'character_leaf') {
                    const st = row.primaryInv ? statusStyles[row.primaryInv.status] || statusStyles.pending : null
                    const extraInvites = row.primaryInv
                      ? row.invites.filter((i) => i.id !== row.primaryInv!.id).length
                      : row.invites.length
                    return (
                      <Box
                        component="tr"
                        key={row.key}
                        sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                      >
                        <Box component="td" sx={{ ...cellSx, verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                            {row.invites.length === 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                                onClick={() => handleInviteForTag(row.tagId, row.name)}
                              >
                                Invite
                              </Button>
                            )}
                            {row.primaryInv && (
                              <>
                                <QrShareInviteButton onClick={() => setShareInvite(row.primaryInv!)} iconSize={16} />
                                <Tooltip title="Delete invitation">
                                  <IconButton
                                    size="small"
                                    aria-label="Delete invitation"
                                    onClick={() => setInviteDeleteConfirmId(row.primaryInv!.id)}
                                    sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {row.user && (
                              <DeleteUserButton
                                onClick={() => setUserDeleteConfirmId(row.user!.id)}
                                iconSize={16}
                                disabled={row.user.id === user.id}
                                disabledReason="You cannot delete your own account"
                              />
                            )}
                            {bookIdForInvites && (
                              <>
                                <Tooltip title="Write about this character">
                                  <IconButton
                                    size="small"
                                    aria-label="Write about this character"
                                    onClick={() => setNoteTarget({ tagId: row.tagId, name: row.name })}
                                    sx={{ color: 'var(--text-tertiary)', '&:hover': { color: 'var(--icon-hover)' } }}
                                  >
                                    <EditNoteIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Open this character's page">
                                  <IconButton
                                    size="small"
                                    aria-label="Open this character's page"
                                    onClick={() => navigate(`/character/${bookIdForInvites}/${row.tagId}`)}
                                    sx={{ color: 'var(--text-tertiary)', '&:hover': { color: 'var(--icon-hover)' } }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Box>
                        <Box component="td" sx={cellSx}>
                          <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.45 }} dir="auto">
                            <Box component="span" sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                              {row.path}
                            </Box>
                            {row.primaryInv && (
                              <Box component="span" sx={inviteRecipientNameSx}>
                                {' · '}
                                {row.primaryInv.recipientName}
                                {extraInvites > 0 ? ` (+${extraInvites})` : ''}
                              </Box>
                            )}
                          </Typography>
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)' }}>
                          {row.user?.email ?? '—'}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)' }}>
                          {row.user ? formatUserLabel(row.user) : '—'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {row.user?.role ?? '—'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {row.primaryInv && extraInvites > 0 ? (
                            <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                              +{extraInvites} more invitation{extraInvites !== 1 ? 's' : ''}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {row.primaryInv ? (row.primaryInv.type === 'character' ? 'Character' : 'Regular') : '—'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {row.primaryInv && st ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }}
                              />
                              {st.label}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {row.primaryInv
                            ? new Date(row.primaryInv.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '—'}
                        </Box>
                      </Box>
                    )
                  }

                  if (row.kind === 'account') {
                    const u = row.user
                    const via = u.invitedVia?.id ? invById.get(u.invitedVia.id) : undefined
                    const st = via ? statusStyles[via.status] || statusStyles.pending : null
                    return (
                      <Box
                        component="tr"
                        key={row.key}
                        sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                      >
                        <Box component="td" sx={{ ...cellSx, verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                            {via && (
                              <>
                                <Tooltip title="Copy invite link">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyLink(via.token)}
                                    sx={{ color: 'var(--icon-default)' }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete invitation">
                                  <IconButton
                                    size="small"
                                    onClick={() => setInviteDeleteConfirmId(via.id)}
                                    sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <DeleteUserButton
                              onClick={() => setUserDeleteConfirmId(u.id)}
                              iconSize={16}
                              disabled={u.id === user.id}
                              disabledReason="You cannot delete your own account"
                            />
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                          — (no character)
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {u.email}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {formatUserLabel(u)}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {u.role}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via ? (
                            <Typography sx={{ fontSize: '0.84rem', ...inviteRecipientNameSx }}>
                              {via.recipientName}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via ? (via.type === 'character' ? 'Character' : 'Regular') : '—'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via && st ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }}
                              />
                              {st.label}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {via
                            ? new Date(via.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '—'}
                        </Box>
                      </Box>
                    )
                  }

                  if (row.kind === 'pending_regular') {
                    const inv = row.inv
                    const st = statusStyles[inv.status] || statusStyles.pending
                    return (
                      <Box
                        component="tr"
                        key={row.key}
                        sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                      >
                        <Box component="td" sx={{ ...cellSx, verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                            <QrShareInviteButton onClick={() => setShareInvite(inv)} iconSize={16} />
                            <Tooltip title="Delete invitation">
                              <IconButton
                                size="small"
                                aria-label="Delete invitation"
                                onClick={() => setInviteDeleteConfirmId(inv.id)}
                                sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-tertiary)' }}>
                          —
                        </Box>
                        <Box component="td" sx={cellSx}>
                          —
                        </Box>
                        <Box component="td" sx={cellSx}>
                          —
                        </Box>
                        <Box component="td" sx={cellSx}>
                          —
                        </Box>
                        <Box component="td" sx={cellSx}>
                          <Typography sx={{ fontSize: '0.84rem', ...inviteRecipientNameSx }}>
                            {inv.recipientName}
                          </Typography>
                        </Box>
                        <Box component="td" sx={cellSx}>
                          Regular
                        </Box>
                        <Box component="td" sx={cellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }}
                            />
                            {st.label}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {new Date(inv.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Box>
                      </Box>
                    )
                  }

                  if (row.kind === 'account_orphan_character') {
                    const u = row.user
                    const via = u.invitedVia?.id ? invById.get(u.invitedVia.id) : undefined
                    const st = via ? statusStyles[via.status] || statusStyles.pending : null
                    return (
                      <Box
                        component="tr"
                        key={row.key}
                        sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                      >
                        <Box component="td" sx={{ ...cellSx, verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                            {via && (
                              <>
                                <Tooltip title="Copy invite link">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyLink(via.token)}
                                    sx={{ color: 'var(--icon-default)' }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete invitation">
                                  <IconButton
                                    size="small"
                                    onClick={() => setInviteDeleteConfirmId(via.id)}
                                    sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <DeleteUserButton
                              onClick={() => setUserDeleteConfirmId(u.id)}
                              iconSize={16}
                              disabled={u.id === user.id}
                              disabledReason="You cannot delete your own account"
                            />
                          </Box>
                        </Box>
                        <Box component="td" sx={cellSx}>
                          <Typography sx={{ fontSize: '0.84rem' }} dir="auto">
                            {row.pathLabel}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', mt: 0.25 }}>
                            stale character tag
                          </Typography>
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {u.email}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {formatUserLabel(u)}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {u.role}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via ? (
                            <Typography sx={{ fontSize: '0.84rem', ...inviteRecipientNameSx }}>
                              {via.recipientName}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via ? (via.type === 'character' ? 'Character' : 'Regular') : '—'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {via && st ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }}
                              />
                              {st.label}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {via
                            ? new Date(via.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '—'}
                        </Box>
                      </Box>
                    )
                  }

                  const inv = row.inv
                  const st = statusStyles[inv.status] || statusStyles.pending
                  return (
                    <Box
                      component="tr"
                      key={row.key}
                      sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                    >
                      <Box component="td" sx={{ ...cellSx, verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                          <QrShareInviteButton onClick={() => setShareInvite(inv)} iconSize={16} />
                          <Tooltip title="Delete invitation">
                            <IconButton
                              size="small"
                              aria-label="Delete invitation"
                              onClick={() => setInviteDeleteConfirmId(inv.id)}
                              sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box component="td" sx={cellSx}>
                        <Typography sx={{ fontSize: '0.84rem' }}>{row.pathLabel}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', mt: 0.25 }}>
                          non-leaf tag
                        </Typography>
                      </Box>
                      <Box component="td" sx={cellSx}>
                        {inv.claimedBy?.email ?? '—'}
                      </Box>
                      <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)' }}>
                        {inv.claimedBy
                          ? `${inv.claimedBy.firstName || ''} ${inv.claimedBy.lastName || ''}`.trim() || '—'
                          : '—'}
                      </Box>
                      <Box component="td" sx={cellSx}>
                        —
                      </Box>
                      <Box component="td" sx={cellSx}>
                        <Typography sx={{ fontSize: '0.84rem', ...inviteRecipientNameSx }} component="span">
                          {inv.recipientName}
                        </Typography>
                      </Box>
                      <Box component="td" sx={cellSx}>
                        Character
                      </Box>
                      <Box component="td" sx={cellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.dotColor, flexShrink: 0 }} />
                          {st.label}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {new Date(inv.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {bookIdForInvites && noteTarget && (
        <CharacterNoteDialog
          open
          bookId={bookIdForInvites}
          tagId={noteTarget.tagId}
          name={noteTarget.name}
          onClose={() => setNoteTarget(null)}
        />
      )}

      <InvitationShareDialog
        open={!!shareInvite}
        onClose={() => setShareInvite(null)}
        recipientName={shareInvite?.recipientName ?? ''}
        token={shareInvite?.token ?? ''}
      />

      <Dialog
        open={inviteDeleteConfirmId != null}
        onClose={() => {
          if (!inviteDeleteSubmitting) {
            setInviteDeleteConfirmId(null)
          }
        }}
        aria-labelledby="people-delete-invite-title"
      >
        <DialogTitle id="people-delete-invite-title">Delete invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {invitePendingDelete
              ? `This will remove the invitation for “${invitePendingDelete.recipientName}”. The link will stop working.`
              : 'This will remove the invitation. The link will stop working.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDeleteConfirmId(null)} disabled={inviteDeleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmDeleteInvitation()}
            disabled={inviteDeleteSubmitting}
            variant="contained"
            color="error"
          >
            {inviteDeleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={userDeleteConfirmId != null}
        onClose={() => {
          if (!userDeleteSubmitting) {
            setUserDeleteConfirmId(null)
          }
        }}
        aria-labelledby="people-delete-user-title"
      >
        <DialogTitle id="people-delete-user-title">Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {userPendingDelete
              ? `This will permanently remove the account for ${userPendingDelete.email}. Any invitations they claimed will revert to pending. Existing invitation history is kept.`
              : 'This will permanently remove the user account.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDeleteConfirmId(null)} disabled={userDeleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmDeleteUser()}
            disabled={userDeleteSubmitting}
            variant="contained"
            color="error"
          >
            {userDeleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PeoplePage
